import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from 'flowbite-react';
import {
  ArrowPathIcon,
  UserGroupIcon,
  CheckCircleIcon,
  StarIcon,
  ChartBarIcon,
  ClockIcon,
  ServerIcon,
  CircleStackIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { analyticsApi, healthCheck } from '../services/api';
import StatCard from '../components/StatCard';
import toast from 'react-hot-toast';

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [systemHealth, setSystemHealth] = useState({
    api: 'checking',
    database: 'checking'
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    loadStats();
    checkSystemHealth();
    loadRecentActivity();
  }, []);

  async function loadStats() {
    try {
      setLoading(true);
      const data = await analyticsApi.overview();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }

  async function checkSystemHealth() {
    try {
      const health = await healthCheck();
      setSystemHealth({
        api: health.status === 'ok' ? 'operational' : 'degraded',
        database: health.status === 'ok' ? 'operational' : 'degraded'
      });
    } catch (error) {
      console.error('Health check failed:', error);
      setSystemHealth({
        api: 'error',
        database: 'error'
      });
    }
  }

  async function loadRecentActivity() {
    try {
      const data = await analyticsApi.activity(5);
      if (data?.activity) {
        setRecentActivity(data.activity.map(item => ({
          id: item.id,
          action: item.action,
          user: item.user,
          time: formatTimeAgo(item.timestamp),
          type: mapActivityType(item.type)
        })));
      }
    } catch (error) {
      console.error('Failed to load recent activity:', error);
      // Silently fail - activity is not critical
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    await Promise.all([loadStats(), checkSystemHealth(), loadRecentActivity()]);
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  }

  // Helper to format timestamp as "X minutes ago"
  function formatTimeAgo(timestamp) {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }

  // Map activity type to icon category
  function mapActivityType(type) {
    const typeMap = {
      'admin_action': 'user',
      'error': 'error',
      'api_call': 'data',
      'page_view': 'data',
      'feature_use': 'data',
      'auth_event': 'user',
      'data_operation': 'data'
    };
    return typeMap[type] || 'data';
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  // Calculate trends (mock data for now - replace with real trend calculations)
  const userTrend = stats?.users?.total > 100 ? { direction: 'up', value: '+12%' } : { direction: 'down', value: '-3%' };
  const activeTrend = { direction: 'up', value: '+8%' };
  const premiumTrend = { direction: 'up', value: '+15%' };
  const entriesTrend = { direction: 'up', value: '+24%' };

  return (
    <div className="animate-fade-in">
      {/* Header with Refresh */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              🔍 DEBUG v1.3.0
            </span>
          </div>
          <p className="mt-1 text-gray-600">Overview of your PeeTrack application</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-white transition hover:bg-primary-600 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* System Health Status */}
      <Card className="mb-6 animate-slide-down">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">System Health</h2>
          <div className="flex gap-6">
            <HealthIndicator
              label="API Status"
              status={systemHealth.api}
              icon={<ServerIcon className="h-5 w-5" />}
            />
            <HealthIndicator
              label="Database"
              status={systemHealth.database}
              icon={<CircleStackIcon className="h-5 w-5" />}
            />
          </div>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <div className="animate-slide-up" style={{ animationDelay: '0ms' }}>
          <StatCard
            title="Total Users"
            value={stats?.users?.total || 0}
            icon={<UserGroupIcon className="h-6 w-6" />}
            color="primary"
            trend={userTrend}
          />
        </div>
        <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
          <StatCard
            title="Active Users (30d)"
            value={stats?.users?.active30d || 0}
            icon={<CheckCircleIcon className="h-6 w-6" />}
            color="success"
            trend={activeTrend}
          />
        </div>
        <div className="animate-slide-up" style={{ animationDelay: '200ms' }}>
          <StatCard
            title="Premium Users"
            value={stats?.users?.premium || 0}
            icon={<StarIcon className="h-6 w-6" />}
            color="warning"
            trend={premiumTrend}
          />
        </div>
        <div className="animate-slide-up" style={{ animationDelay: '300ms' }}>
          <StatCard
            title="Data Entries"
            value={stats?.data?.totalEntries || 0}
            icon={<ChartBarIcon className="h-6 w-6" />}
            color="primary"
            trend={entriesTrend}
          />
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Quick Links - 2 columns on large screens */}
        <div className="lg:col-span-2">
          <Card className="h-full">
            <h2 className="mb-4 text-xl font-bold text-gray-900">Quick Actions</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <QuickLink
                to="/users"
                title="User Management"
                description="View and manage all users"
                icon={<UserGroupIcon className="h-6 w-6" />}
                color="blue"
              />
              <QuickLink
                to="/analytics"
                title="Analytics"
                description="View detailed usage analytics"
                icon={<ChartBarIcon className="h-6 w-6" />}
                color="purple"
              />
              <QuickLink
                to="/errors"
                title="Error Tracking"
                description="Monitor application errors"
                icon={<ServerIcon className="h-6 w-6" />}
                color="red"
              />
              <QuickLink
                to="/database"
                title="Database Stats"
                description="View database size and performance"
                icon={<CircleStackIcon className="h-6 w-6" />}
                color="green"
              />
            </div>
          </Card>
        </div>

        {/* Recent Activity - 1 column on large screens */}
        <div>
          <Card className="h-full">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
              <ClockIcon className="h-5 w-5 text-gray-400" />
            </div>
            <div className="space-y-3">
              {recentActivity.map((activity, index) => (
                <ActivityItem
                  key={activity.id}
                  activity={activity}
                  delay={index * 50}
                />
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Database Info */}
      {stats?.database && (
        <Card className="mt-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-blue-50 p-3 text-blue-600">
                <CircleStackIcon className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Database Storage</h2>
                <p className="text-2xl font-bold text-primary">
                  {stats.database.size}
                </p>
                <p className="text-sm text-gray-600">Total database size</p>
              </div>
            </div>
            <Link
              to="/database"
              className="flex items-center gap-1 text-sm font-medium text-primary hover:text-primary-600"
            >
              View Details
              <ChevronRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </Card>
      )}
    </div>
  );
}

function HealthIndicator({ label, status, icon }) {
  const statusConfig = {
    operational: {
      color: 'text-green-600',
      bg: 'bg-green-50',
      label: 'Operational'
    },
    degraded: {
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
      label: 'Degraded'
    },
    error: {
      color: 'text-red-600',
      bg: 'bg-red-50',
      label: 'Error'
    },
    checking: {
      color: 'text-gray-600',
      bg: 'bg-gray-50',
      label: 'Checking...'
    }
  };

  const config = statusConfig[status] || statusConfig.checking;

  return (
    <div className="flex items-center gap-2">
      <div className={`rounded-full p-2 ${config.bg} ${config.color}`}>
        {icon}
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className={`text-sm font-semibold ${config.color}`}>
          {config.label}
        </p>
      </div>
    </div>
  );
}

function QuickLink({ to, title, description, icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 group-hover:bg-blue-100',
    purple: 'bg-purple-50 text-purple-600 group-hover:bg-purple-100',
    red: 'bg-red-50 text-red-600 group-hover:bg-red-100',
    green: 'bg-green-50 text-green-600 group-hover:bg-green-100'
  };

  return (
    <Link
      to={to}
      className="group block rounded-lg border border-gray-200 bg-white p-4 transition hover:border-primary hover:shadow-md"
    >
      <div className="flex items-start gap-3">
        <div className={`rounded-lg p-2 transition ${colorClasses[color]}`}>
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-900 group-hover:text-primary">
            {title}
          </h3>
          <p className="mt-1 text-sm text-gray-600">{description}</p>
        </div>
        <ChevronRightIcon className="h-5 w-5 text-gray-400 transition group-hover:translate-x-1 group-hover:text-primary" />
      </div>
    </Link>
  );
}

function ActivityItem({ activity, delay }) {
  const typeConfig = {
    user: { icon: <UserGroupIcon className="h-4 w-4" />, color: 'bg-blue-50 text-blue-600' },
    subscription: { icon: <StarIcon className="h-4 w-4" />, color: 'bg-yellow-50 text-yellow-600' },
    data: { icon: <ChartBarIcon className="h-4 w-4" />, color: 'bg-purple-50 text-purple-600' },
    error: { icon: <ServerIcon className="h-4 w-4" />, color: 'bg-red-50 text-red-600' }
  };

  const config = typeConfig[activity.type] || typeConfig.user;

  return (
    <div
      className="flex items-start gap-3 rounded-lg border border-gray-100 p-3 transition hover:border-gray-200 hover:bg-gray-50 animate-slide-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className={`rounded-lg p-2 ${config.color}`}>
        {config.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{activity.action}</p>
        <p className="text-xs text-gray-500 truncate">{activity.user}</p>
        <p className="text-xs text-gray-400">{activity.time}</p>
      </div>
    </div>
  );
}
